/**
 * スクロール入場アニメーションの IntersectionObserver フォールバック。
 *
 * ホームの入場演出は CSS Scroll-driven Animations（animation-timeline: view()）
 * が一次実装。ここは非対応ブラウザ（Safari等）向けのフォールバックで、
 * `.reveal` クラスを持つ要素に v-reveal を付けると、ビューポート進入時に
 * `.revealed` を one-shot 付与する。
 *
 * 二重発火しない仕組み:
 * - CSS側の `.reveal` スタイルは @supports not (animation-timeline: view())
 *   で囲まれているため、scroll-driven 対応ブラウザでは何も起きない
 * - JS側も supportsScrollTimeline() が真なら observer を作らない
 * - prefers-reduced-motion 時は CSS/JS とも無効（即表示）
 */

/** CSS Scroll-driven Animations 対応判定 */
export function supportsScrollTimeline() {
  return (
    typeof CSS !== "undefined" && CSS.supports("animation-timeline: view()")
  );
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// 全要素で共有する one-shot Observer
let observer = null;

function getObserver() {
  if (observer) return observer;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        observer.unobserve(el);
        el.classList.add("revealed");
        // 入場完了後は reveal/revealed を外して素の状態に戻す
        // （.reveal の transition がホバー演出等と競合しないように）
        const cleanup = () => {
          el.classList.remove("reveal", "revealed");
          el.removeEventListener("transitionend", onEnd);
          clearTimeout(timer);
        };
        const onEnd = (e) => {
          if (e.target === el && e.propertyName === "opacity") cleanup();
        };
        el.addEventListener("transitionend", onEnd);
        const timer = setTimeout(cleanup, 1000); // transitionend が来ない場合の保険
      }
    },
    // 既存 scroll-driven の animation-range: entry 10% に体感を合わせる
    { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
  );
  return observer;
}

/**
 * v-reveal ディレクティブ。
 * 使い方: <div class="stat-item reveal" v-reveal>
 */
export const vReveal = {
  mounted(el) {
    // scroll-driven 対応ならCSSに任せる / reduced-motion なら即表示（CSSが無効）
    if (supportsScrollTimeline() || prefersReducedMotion()) return;
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("revealed"); // 最終フォールバック: 即表示
      return;
    }
    getObserver().observe(el);
  },
  unmounted(el) {
    observer?.unobserve(el);
  },
};

/**
 * 要素が初めてビューポートに入ったら callback を1回だけ呼ぶ。
 * カウントアップ等、CSSに依存しない演出のトリガー用。
 * reduced-motion / IO 非搭載時は即時実行する。
 */
export function observeOnce(el, callback, options = {}) {
  if (
    prefersReducedMotion() ||
    typeof IntersectionObserver === "undefined"
  ) {
    callback();
    return () => {};
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        callback();
      }
    },
    { threshold: 0.3, ...options }
  );
  io.observe(el);
  return () => io.disconnect();
}
